import { Controller } from "@hotwired/stimulus";

const EXPAND_EVENT = "question-card:expand";

const ACTIVE_CLASSES = [
  "bg-[#F0F6F6]",
  "border-primary",
  "text-primary",
  "font-semibold",
];

const INACTIVE_CLASSES = ["text-slate-500", "hover:bg-slate-50"];

// Text color flips on the title button itself.
const SECTION_BUTTON_ACTIVE_CLASSES = ["text-primary"];
const SECTION_BUTTON_INACTIVE_CLASSES = ["text-slate-700", "hover:text-primary"];
// Background flips on the surrounding <summary> so the whole row highlights.
const SECTION_ROW_ACTIVE_CLASSES = ["bg-[#F0F6F6]"];
const SECTION_ROW_INACTIVE_CLASSES = ["hover:bg-slate-50"];

const SECTION_SELECT_EVENT = "outline-rail:section-select";

export default class extends Controller<HTMLElement> {
  static targets = [
    "item",
    "sectionItem",
    "newSectionForm",
    "newSectionInput",
    "empty",
  ];

  declare readonly itemTargets: HTMLElement[];
  declare readonly sectionItemTargets: HTMLElement[];
  declare readonly hasNewSectionFormTarget: boolean;
  declare readonly newSectionFormTarget: HTMLFormElement;
  declare readonly hasNewSectionInputTarget: boolean;
  declare readonly newSectionInputTarget: HTMLInputElement;
  declare readonly hasEmptyTarget: boolean;
  declare readonly emptyTarget: HTMLElement;

  private boundOnCardExpand = this.onCardExpand.bind(this);
  private boundOnSectionSelect = this.onSectionSelect.bind(this);
  private submitInFlight = false;

  connect() {
    window.addEventListener(EXPAND_EVENT, this.boundOnCardExpand);
    window.addEventListener(SECTION_SELECT_EVENT, this.boundOnSectionSelect);
  }

  disconnect() {
    window.removeEventListener(EXPAND_EVENT, this.boundOnCardExpand);
    window.removeEventListener(
      SECTION_SELECT_EVENT,
      this.boundOnSectionSelect,
    );
  }

  selectQuestion(event: Event) {
    const item = event.currentTarget as HTMLElement;
    const questionId = item.dataset.questionId;
    if (!questionId) return;

    const card = document.querySelector<HTMLElement>(
      `[data-controller~="question-card"][data-question-id="${questionId}"]`,
    );
    if (!card) return;

    card.scrollIntoView({ behavior: "smooth", block: "start" });

    const display = card.querySelector<HTMLElement>(
      '[data-question-card-target="display"]',
    );
    display?.click();
  }

  selectSection(event: Event) {
    const item = event.currentTarget as HTMLElement;
    const sectionId = item.dataset.sectionId;
    if (!sectionId) return;

    window.dispatchEvent(
      new CustomEvent(SECTION_SELECT_EVENT, {
        detail: { sectionId, source: item },
      }),
    );
  }

  addSection() {
    if (!this.hasNewSectionFormTarget || !this.hasNewSectionInputTarget) return;
    this.newSectionInputTarget.value = "";
    this.newSectionFormTarget.classList.remove("hidden");
    this.newSectionInputTarget.focus();
  }

  commitNewSection() {
    if (!this.hasNewSectionFormTarget || !this.hasNewSectionInputTarget) return;
    if (this.submitInFlight) return;
    const value = this.newSectionInputTarget.value.trim();
    if (value === "") {
      this.cancelNewSection();
      return;
    }
    this.submitInFlight = true;
    this.newSectionFormTarget.requestSubmit();
  }

  cancelNewSection() {
    if (!this.hasNewSectionFormTarget || !this.hasNewSectionInputTarget) return;
    this.newSectionInputTarget.value = "";
    this.newSectionFormTarget.classList.add("hidden");
  }

  newSectionSubmitEnd(event: Event) {
    this.submitInFlight = false;
    const detail = (event as CustomEvent).detail as
      | { success?: boolean }
      | undefined;
    if (detail?.success) {
      this.cancelNewSection();
    }
  }

  private onCardExpand(event: Event) {
    const detail = (event as CustomEvent).detail;
    const questionId = detail?.questionId as string | undefined;
    this.highlight(questionId);
    // Selecting a question clears any active section highlight — the right
    // rail can only display one settings panel at a time.
    this.highlightSection(undefined);
  }

  private onSectionSelect(event: Event) {
    const detail = (event as CustomEvent).detail;
    const sectionId = detail?.sectionId as string | undefined;
    this.highlightSection(sectionId);
    this.highlight(undefined);
  }

  private highlight(questionId: string | undefined) {
    this.itemTargets.forEach((item) => {
      const isActive = !!questionId && item.dataset.questionId === questionId;
      ACTIVE_CLASSES.forEach((cls) => item.classList.toggle(cls, isActive));
      INACTIVE_CLASSES.forEach((cls) => item.classList.toggle(cls, !isActive));
    });
  }

  private highlightSection(sectionId: string | undefined) {
    this.sectionItemTargets.forEach((item) => {
      const isActive = !!sectionId && item.dataset.sectionId === sectionId;

      SECTION_BUTTON_ACTIVE_CLASSES.forEach((cls) =>
        item.classList.toggle(cls, isActive),
      );
      SECTION_BUTTON_INACTIVE_CLASSES.forEach((cls) =>
        item.classList.toggle(cls, !isActive),
      );

      const summary = item.closest("summary");
      if (!summary) return;
      SECTION_ROW_ACTIVE_CLASSES.forEach((cls) =>
        summary.classList.toggle(cls, isActive),
      );
      SECTION_ROW_INACTIVE_CLASSES.forEach((cls) =>
        summary.classList.toggle(cls, !isActive),
      );
    });
  }
}
